import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  FormControl,
  InputLabel,
  Select,
  useTheme as useMuiTheme,
  useMediaQuery
} from '@mui/material';
import {
  ArrowBack,
  CheckCircle,
  Cancel,
  MyLocation,
  Warning,
  ThumbUp,
  ThumbDown,
  MoreVert,
  Refresh,
  Label,
  Face,
  History
} from '@mui/icons-material';
import { useTheme } from '../context/ThemeContext';
import { vendorStewardReportApi, AttendanceConfig, PendingApproval, AuditLog, FlagOption } from '../services/attendanceService';
import { format, subDays } from 'date-fns';
import toast from 'react-hot-toast';
import { AppIconButton } from '../components/ui';
import { CURRENCY } from '../config/currency';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`report-tabpanel-${index}`}
      aria-labelledby={`report-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

// Filter chip component matching VendorOrders style
interface FilterChipProps {
  label: string;
  count?: number;
  icon: string;
  isActive: boolean;
  onClick: () => void;
  color?: string;
}

function FilterChip({ label, count, icon, isActive, onClick, color = '#64748B' }: FilterChipProps) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: 0,
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        flexShrink: 0
      }}
    >
      <span
        style={{
          padding: '6px 12px',
          fontSize: '0.8125rem',
          fontWeight: 600,
          borderRadius: '6px',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          display: 'inline-flex',
          alignItems: 'center',
          transition: 'all 0.2s',
          backgroundColor: isActive ? color : '#F1F5F9', /* basil-todo */
          color: isActive ? '#FFFFFF' : '#64748B',
          borderWidth: isActive ? '2px' : '1px',
          borderStyle: 'solid',
          borderColor: isActive ? color : '#CBD5E1', /* basil-todo */
          transform: isActive ? 'scale(1.02)' : 'scale(1)',
          boxShadow: isActive ? `0 2px 4px ${color}30` : 'none'
        }}
      >
        <span style={{ marginRight: '4px', fontSize: '10px', lineHeight: 1 }}>{icon}</span>
        {label}{count !== undefined ? `: ${count}` : ''}
      </span>
    </button>
  );
}

// Date preset chip
interface DatePresetChipProps {
  label: string;
  isActive: boolean;
  onClick: () => void;
}

function DatePresetChip({ label, isActive, onClick }: DatePresetChipProps) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '4px 10px',
        fontSize: '11px',
        fontWeight: 500,
        borderRadius: '4px',
        border: 'none',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        backgroundColor: isActive ? '#3B82F6' : '#E2E8F0',
        color: isActive ? '#FFFFFF' : '#475569',
        transition: 'all 0.15s'
      }}
    >
      {label}
    </button>
  );
}

export default function VendorStewardReportPage() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string>(format(subDays(new Date(), 7), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
  const [datePreset, setDatePreset] = useState<string>('7days');

  // Data
  const [todayAttendance, setTodayAttendance] = useState<any>(null);
  const [performanceData, setPerformanceData] = useState<any>(null);
  const [attendanceData, setAttendanceData] = useState<any>(null);
  const [venueLocation, setVenueLocation] = useState<any>(null);
  const [config, setConfig] = useState<AttendanceConfig | null>(null);
  const [pendingApprovals, setPendingApprovals] = useState<PendingApproval[]>([]);
  const [auditHistory, setAuditHistory] = useState<AuditLog[]>([]);
  const [flagOptions, setFlagOptions] = useState<FlagOption[]>([]);

  // Location dialog
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [newLatitude, setNewLatitude] = useState('');
  const [newLongitude, setNewLongitude] = useState('');
  const [newRadius, setNewRadius] = useState('100');
  const [savingLocation, setSavingLocation] = useState(false);

  // Actions menu
  const [actionsAnchorEl, setActionsAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedSteward, setSelectedSteward] = useState<any>(null);

  // Dialogs for admin actions
  const [resetCheckInDialogOpen, setResetCheckInDialogOpen] = useState(false);
  const [resetCheckOutDialogOpen, setResetCheckOutDialogOpen] = useState(false);
  const [resetFaceDialogOpen, setResetFaceDialogOpen] = useState(false);
  const [addFlagDialogOpen, setAddFlagDialogOpen] = useState(false);
  const [viewFlagsDialogOpen, setViewFlagsDialogOpen] = useState(false);
  const [actionReason, setActionReason] = useState('');
  const [selectedFlag, setSelectedFlag] = useState('');
  const [flagNotes, setFlagNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [currentFlags, setCurrentFlags] = useState<string[]>([]);

  // Date preset handlers
  const handleDatePreset = (preset: string) => {
    setDatePreset(preset);
    const today = new Date();
    switch (preset) {
      case 'today':
        setStartDate(format(today, 'yyyy-MM-dd'));
        setEndDate(format(today, 'yyyy-MM-dd'));
        break;
      case 'yesterday':
        const yesterday = subDays(today, 1);
        setStartDate(format(yesterday, 'yyyy-MM-dd'));
        setEndDate(format(yesterday, 'yyyy-MM-dd'));
        break;
      case '7days':
        setStartDate(format(subDays(today, 7), 'yyyy-MM-dd'));
        setEndDate(format(today, 'yyyy-MM-dd'));
        break;
      case '30days':
        setStartDate(format(subDays(today, 30), 'yyyy-MM-dd'));
        setEndDate(format(today, 'yyyy-MM-dd'));
        break;
      case 'custom':
        // Keep current dates
        break;
    }
  };

  useEffect(() => {
    loadData();
    loadFlagOptions();
  }, []);

  useEffect(() => {
    if (tabValue === 2) {
      loadPerformance();
    } else if (tabValue === 3) {
      loadAttendanceReport();
    } else if (tabValue === 5) {
      loadAuditHistory();
    }
  }, [tabValue, startDate, endDate]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [todayData, locationData, configData, pendingData] = await Promise.all([
        vendorStewardReportApi.getTodayAttendance(),
        vendorStewardReportApi.getVenueLocation(),
        vendorStewardReportApi.getConfig(),
        vendorStewardReportApi.getPendingApprovals()
      ]);

      setTodayAttendance(todayData);
      setVenueLocation(locationData);
      setConfig(configData);
      setPendingApprovals(pendingData.pendingApprovals || []);

      if (locationData.configured) {
        setNewLatitude(locationData.latitude?.toString() || '');
        setNewLongitude(locationData.longitude?.toString() || '');
        setNewRadius(locationData.radiusMeters?.toString() || '100');
      }
    } catch (err) {
      setError((err as Error).message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadFlagOptions = async () => {
    try {
      const data = await vendorStewardReportApi.getFlagOptions();
      setFlagOptions(data.flagOptions || []);
    } catch (err) {
      console.error('Failed to load flag options:', err);
    }
  };

  const loadPerformance = async () => {
    try {
      setLoading(true);
      const data = await vendorStewardReportApi.getPerformanceReport(startDate, endDate);
      setPerformanceData(data);
    } catch (err) {
      toast.error('Failed to load performance data');
    } finally {
      setLoading(false);
    }
  };

  const loadAttendanceReport = async () => {
    try {
      setLoading(true);
      const data = await vendorStewardReportApi.getAttendanceReport(startDate, endDate);
      setAttendanceData(data);
    } catch (err) {
      toast.error('Failed to load attendance data');
    } finally {
      setLoading(false);
    }
  };

  const loadAuditHistory = async () => {
    try {
      setLoading(true);
      const data = await vendorStewardReportApi.getAuditHistory(100);
      setAuditHistory(data.auditLogs || []);
    } catch (err) {
      toast.error('Failed to load audit history');
    } finally {
      setLoading(false);
    }
  };

  const handleGetCurrentLocation = () => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setNewLatitude(position.coords.latitude.toString());
        setNewLongitude(position.coords.longitude.toString());
        toast.success('Location captured!');
      },
      (err) => {
        toast.error('Failed to get location: ' + err.message);
      },
      { enableHighAccuracy: true }
    );
  };

  const handleSaveLocation = async () => {
    if (!newLatitude || !newLongitude) {
      toast.error('Please enter latitude and longitude');
      return;
    }

    try {
      setSavingLocation(true);
      await vendorStewardReportApi.setVenueLocation(
        parseFloat(newLatitude),
        parseFloat(newLongitude),
        parseInt(newRadius)
      );
      toast.success('Venue location saved!');
      setLocationDialogOpen(false);
      loadData();
    } catch (err) {
      toast.error((err as Error).message || 'Failed to save location');
    } finally {
      setSavingLocation(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(CURRENCY.locale, {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const handleApprove = async (id: number) => {
    try {
      await vendorStewardReportApi.approveAttendance(id);
      toast.success('Attendance approved');
      loadData();
    } catch (err) {
      toast.error((err as Error).message || 'Failed to approve');
    }
  };

  const handleReject = async (id: number) => {
    const reason = prompt('Enter rejection reason:');
    if (!reason) return;
    try {
      await vendorStewardReportApi.rejectAttendance(id, reason);
      toast.success('Attendance rejected');
      loadData();
    } catch (err) {
      toast.error((err as Error).message || 'Failed to reject');
    }
  };

  const handleConfigChange = async (key: string, value: any) => {
    try {
      await vendorStewardReportApi.updateConfig({ [key]: value });
      setConfig(prev => prev ? { ...prev, [key]: value } : null);
      toast.success('Setting updated');
    } catch (err) {
      toast.error((err as Error).message || 'Failed to update setting');
    }
  };

  // Actions menu handlers
  const handleActionsClick = (event: React.MouseEvent<HTMLElement>, steward: any) => {
    setActionsAnchorEl(event.currentTarget);
    setSelectedSteward(steward);
  };

  const handleActionsClose = () => {
    setActionsAnchorEl(null);
  };

  const openResetCheckInDialog = () => {
    handleActionsClose();
    setActionReason('');
    setResetCheckInDialogOpen(true);
  };

  const openResetCheckOutDialog = () => {
    handleActionsClose();
    setActionReason('');
    setResetCheckOutDialogOpen(true);
  };

  const openResetFaceDialog = () => {
    handleActionsClose();
    setActionReason('');
    setResetFaceDialogOpen(true);
  };

  const openAddFlagDialog = () => {
    handleActionsClose();
    setSelectedFlag('');
    setFlagNotes('');
    setAddFlagDialogOpen(true);
  };

  const openViewFlagsDialog = async () => {
    handleActionsClose();
    if (selectedSteward?.attendanceId) {
      try {
        const data = await vendorStewardReportApi.getFlags(selectedSteward.attendanceId);
        setCurrentFlags(data.flags || []);
        setViewFlagsDialogOpen(true);
      } catch (err) {
        toast.error('Failed to load flags');
      }
    }
  };

  const handleResetCheckIn = async () => {
    if (!actionReason.trim()) {
      toast.error('Reason is required');
      return;
    }
    try {
      setActionLoading(true);
      await vendorStewardReportApi.resetCheckIn(selectedSteward.attendanceId, actionReason);
      toast.success('Check-in has been reset');
      setResetCheckInDialogOpen(false);
      loadData();
    } catch (err) {
      toast.error((err as Error).message || 'Failed to reset check-in');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResetCheckOut = async () => {
    if (!actionReason.trim()) {
      toast.error('Reason is required');
      return;
    }
    try {
      setActionLoading(true);
      await vendorStewardReportApi.resetCheckOut(selectedSteward.attendanceId, actionReason);
      toast.success('Check-out has been reset');
      setResetCheckOutDialogOpen(false);
      loadData();
    } catch (err) {
      toast.error((err as Error).message || 'Failed to reset check-out');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResetFace = async () => {
    if (!actionReason.trim()) {
      toast.error('Reason is required');
      return;
    }
    try {
      setActionLoading(true);
      await vendorStewardReportApi.resetFace(selectedSteward.stewardId, actionReason);
      toast.success('Face data has been reset');
      setResetFaceDialogOpen(false);
      loadData();
    } catch (err) {
      toast.error((err as Error).message || 'Failed to reset face');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddFlag = async () => {
    if (!selectedFlag) {
      toast.error('Please select a flag');
      return;
    }
    try {
      setActionLoading(true);
      await vendorStewardReportApi.addFlag(selectedSteward.attendanceId, selectedFlag, flagNotes);
      toast.success('Flag added successfully');
      setAddFlagDialogOpen(false);
      loadData();
    } catch (err) {
      toast.error((err as Error).message || 'Failed to add flag');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveFlag = async (flag: string) => {
    try {
      await vendorStewardReportApi.removeFlag(selectedSteward.attendanceId, flag);
      toast.success('Flag removed');
      const data = await vendorStewardReportApi.getFlags(selectedSteward.attendanceId);
      setCurrentFlags(data.flags || []);
      loadData();
    } catch (err) {
      toast.error((err as Error).message || 'Failed to remove flag');
    }
  };

  const getActionTypeLabel = (actionType: string) => {
    const labels: Record<string, string> = {
      'RESET_CHECK_IN': 'Reset Check-in',
      'RESET_CHECK_OUT': 'Reset Check-out',
      'RESET_FACE': 'Reset Face ID',
      'FLAG_ADDED': 'Flag Added',
      'FLAG_REMOVED': 'Flag Removed',
      'MANUAL_APPROVAL': 'Manual Approval',
      'REJECTION': 'Rejection',
      'MANUAL_CORRECTION': 'Manual Correction'
    };
    return labels[actionType] || actionType;
  };

  const getActionTypeColor = (actionType: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    const colors: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
      'RESET_CHECK_IN': 'warning',
      'RESET_CHECK_OUT': 'warning',
      'RESET_FACE': 'error',
      'FLAG_ADDED': 'info',
      'FLAG_REMOVED': 'default',
      'MANUAL_APPROVAL': 'success',
      'REJECTION': 'error',
      'MANUAL_CORRECTION': 'warning'
    };
    return colors[actionType] || 'default';
  };

  // Date filter component - reusable across tabs
  const DateFilterBar = () => (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      mb: 2,
      flexWrap: 'wrap',
      p: 1.5,
      backgroundColor: 'var(--bg-sunken)',
      borderRadius: 1,
      border: '1px solid var(--border-subtle)'
    }}>
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        <DatePresetChip label="Today" isActive={datePreset === 'today'} onClick={() => handleDatePreset('today')} />
        <DatePresetChip label="Yesterday" isActive={datePreset === 'yesterday'} onClick={() => handleDatePreset('yesterday')} />
        <DatePresetChip label="Last 7 Days" isActive={datePreset === '7days'} onClick={() => handleDatePreset('7days')} />
        <DatePresetChip label="Last 30 Days" isActive={datePreset === '30days'} onClick={() => handleDatePreset('30days')} />
        <DatePresetChip label="Custom" isActive={datePreset === 'custom'} onClick={() => handleDatePreset('custom')} />
      </Box>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <TextField
          type="date"
          value={startDate}
          onChange={(e) => { setStartDate(e.target.value); setDatePreset('custom'); }}
          size="small"
          sx={{ width: { xs: '100%', sm: 140 } }}
          InputProps={{ sx: { fontSize: '0.875rem' } }}
        />
        <Typography variant="body2" color="text.secondary">to</Typography>
        <TextField
          type="date"
          value={endDate}
          onChange={(e) => { setEndDate(e.target.value); setDatePreset('custom'); }}
          size="small"
          sx={{ width: { xs: '100%', sm: 140 } }}
          InputProps={{ sx: { fontSize: '0.875rem' } }}
        />
      </Box>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: theme.background, pb: 4 }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          position: 'sticky',
          top: 0,
          zIndex: 10
        }}
      >
        <Box sx={{ px: { xs: 2, sm: 3 }, py: { xs: 1.5, sm: 2 } }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1.5}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => navigate('/vendor/settings')}
                startIcon={<ArrowBack sx={{ fontSize: 16 }} />}
                sx={{
                  textTransform: 'none',
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  borderColor: theme.gray?.[300] || '#E0E0E0',
                  color: theme.text,
                  backgroundColor: 'transparent',
                  '&:hover': {
                    backgroundColor: theme.hover,
                    borderColor: theme.primary
                  },
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}
              >
                Back
              </Button>
              <Typography variant="h6" sx={{ fontWeight: 600, display: { xs: 'none', sm: 'block' } }}>
                Steward Reports
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, display: { xs: 'block', sm: 'none' } }}>
                Reports
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setLocationDialogOpen(true)}
              startIcon={<MyLocation sx={{ fontSize: 16 }} />}
              sx={{
                textTransform: 'none',
                fontSize: '0.8125rem',
                fontWeight: 600,
                borderColor: theme.gray?.[300] || '#E0E0E0',
                color: theme.text,
                backgroundColor: 'transparent',
                '&:hover': {
                  backgroundColor: theme.hover,
                  borderColor: theme.primary
                }
              }}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                {venueLocation?.configured ? 'Update Location' : 'Set Location'}
              </Box>
              <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                Location
              </Box>
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Filter Chips - VendorOrders Style */}
      <Paper
        elevation={0}
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          px: { xs: 2, sm: 3 },
          py: 1.5
        }}
      >
        <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 0.5 }}>
          <FilterChip
            label="TODAY"
            count={todayAttendance?.totalStewards}
            icon="✓"
            isActive={tabValue === 0}
            onClick={() => setTabValue(0)}
            color="#6366F1"
          />
          <FilterChip
            label="APPROVALS"
            count={pendingApprovals.length}
            icon="⚠"
            isActive={tabValue === 1}
            onClick={() => setTabValue(1)}
            color={pendingApprovals.length > 0 ? '#F59E0B' : '#64748B'}
          />
          <FilterChip
            label="PERFORMANCE"
            icon="📊"
            isActive={tabValue === 2}
            onClick={() => setTabValue(2)}
            color="#64748B"
          />
          <FilterChip
            label="HISTORY"
            icon="📅"
            isActive={tabValue === 3}
            onClick={() => setTabValue(3)}
            color="#64748B"
          />
          <FilterChip
            label="SETTINGS"
            icon="⚙"
            isActive={tabValue === 4}
            onClick={() => setTabValue(4)}
            color="#64748B"
          />
          <FilterChip
            label="AUDIT"
            icon="📋"
            isActive={tabValue === 5}
            onClick={() => setTabValue(5)}
            color="#64748B"
          />
        </Box>
      </Paper>

      {/* Content */}
      <Box sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Date Filter Bar - show on Performance, History, and Audit tabs */}
        {(tabValue === 2 || tabValue === 3 || tabValue === 5) && <DateFilterBar />}

        {/* Today's Attendance */}
        <TabPanel value={tabValue} index={0}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : todayAttendance ? (
            <Box>
              {/* Summary */}
              <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <Paper sx={{ px: 3, py: 2, flex: 1, minWidth: 150 }}>
                  <Typography variant="caption" color="text.secondary">Total Stewards</Typography>
                  <Typography variant="h4">{todayAttendance.totalStewards}</Typography>
                </Paper>
                <Paper sx={{ px: 3, py: 2, flex: 1, minWidth: 150 }}>
                  <Typography variant="caption" color="text.secondary">Checked In</Typography>
                  <Typography variant="h4" color="success.main">{todayAttendance.checkedInCount}</Typography>
                </Paper>
                <Paper sx={{ px: 3, py: 2, flex: 1, minWidth: 150 }}>
                  <Typography variant="caption" color="text.secondary">Not Checked In</Typography>
                  <Typography variant="h4" color="warning.main">
                    {todayAttendance.totalStewards - todayAttendance.checkedInCount}
                  </Typography>
                </Paper>
              </Box>

              {/* Steward List */}
              <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                <Table sx={{ minWidth: 600 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>Steward</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>Code</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>Status</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>Check In</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>Check Out</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {todayAttendance.stewards?.map((steward: any) => (
                      <TableRow key={steward.stewardId}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{steward.stewardName}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{steward.stewardCode}</TableCell>
                        <TableCell>
                          {steward.checkedIn ? (
                            <Chip
                              icon={<CheckCircle />}
                              label={steward.checkedOut ? 'Completed' : 'On Duty'}
                              color={steward.checkedOut ? 'default' : 'success'}
                              size="small"
                            />
                          ) : (
                            <Chip
                              icon={<Cancel />}
                              label="Not Checked In"
                              color="warning"
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {steward.checkInTime
                            ? format(new Date(steward.checkInTime), 'hh:mm a')
                            : '-'}
                        </TableCell>
                        <TableCell>
                          {steward.checkOutTime
                            ? format(new Date(steward.checkOutTime), 'hh:mm a')
                            : '-'}
                        </TableCell>
                        <TableCell align="right">
                          <AppIconButton
                            icon={<MoreVert />}
                            tooltip="Actions"
                            variant="ghost"
                            size="small"
                            onClick={(e) => handleActionsClick(e, steward)}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ) : (
            <Alert severity="info">No stewards found.</Alert>
          )}
        </TabPanel>

        {/* Pending Approvals */}
        <TabPanel value={tabValue} index={1}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : pendingApprovals.length === 0 ? (
            <Alert severity="success">No pending approvals. All attendance records are approved.</Alert>
          ) : (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                {pendingApprovals.length} attendance record(s) require your approval.
              </Alert>
              {/* Mobile view - Cards */}
              <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 2 }}>
                {pendingApprovals.map((approval) => (
                  <Paper key={approval.id} sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600}>{approval.stewardName}</Typography>
                        <Typography variant="caption" color="text.secondary">{approval.stewardCode}</Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="body2">{approval.date}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {format(new Date(approval.checkInTime), 'hh:mm a')}
                        </Typography>
                      </Box>
                    </Box>
                    {approval.flags && approval.flags.length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                        {approval.flags.map((flag, i) => {
                          // Shorten flag labels for mobile
                          let shortLabel = flag;
                          if (flag.includes('FACE_MISMATCH')) shortLabel = 'Face mismatch';
                          else if (flag.includes('OUTSIDE_GEOFENCE')) shortLabel = 'Location issue';
                          else if (flag.includes('LOW_FACE_CONFIDENCE')) shortLabel = 'Low confidence';
                          else if (flag.includes('FACE_NOT_PROVIDED')) shortLabel = 'No face';
                          return <Chip key={i} label={shortLabel} size="small" color="warning" variant="outlined" />;
                        })}
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={<ThumbUp sx={{ fontSize: 16 }} />}
                        onClick={() => handleApprove(approval.id)}
                        fullWidth
                        sx={{ textTransform: 'none' }}
                      >
                        Approve
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<ThumbDown sx={{ fontSize: 16 }} />}
                        onClick={() => handleReject(approval.id)}
                        fullWidth
                        sx={{ textTransform: 'none' }}
                      >
                        Reject
                      </Button>
                    </Box>
                  </Paper>
                ))}
              </Box>
              {/* Desktop view - Table */}
              <TableContainer component={Paper} sx={{ display: { xs: 'none', md: 'block' }, overflowX: 'auto' }}>
                <Table sx={{ minWidth: 600 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>Steward</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>Date</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>Check-in</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>Flags</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pendingApprovals.map((approval) => (
                      <TableRow key={approval.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>{approval.stewardName}</Typography>
                          <Typography variant="caption" color="text.secondary">{approval.stewardCode}</Typography>
                        </TableCell>
                        <TableCell>{approval.date}</TableCell>
                        <TableCell>{format(new Date(approval.checkInTime), 'hh:mm a')}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {approval.flags?.map((flag, i) => (
                              <Chip key={i} label={flag} size="small" color="warning" variant="outlined" />
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              startIcon={<ThumbUp />}
                              onClick={() => handleApprove(approval.id)}
                              sx={{ textTransform: 'none' }}
                            >
                              Approve
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<ThumbDown />}
                              onClick={() => handleReject(approval.id)}
                              sx={{ textTransform: 'none' }}
                            >
                              Reject
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </TabPanel>

        {/* Performance Report */}
        <TabPanel value={tabValue} index={2}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : performanceData ? (
            <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: 550 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>Steward</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>Code</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Orders</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Sales</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {performanceData.stewards?.map((steward: any) => (
                    <TableRow key={steward.stewardId}>
                      <TableCell>{steward.stewardName}</TableCell>
                      <TableCell>{steward.stewardCode}</TableCell>
                      <TableCell align="right">{steward.totalOrders}</TableCell>
                      <TableCell align="right">{formatCurrency(steward.totalSales)}</TableCell>
                      <TableCell>
                        <Chip
                          label={steward.isOnDuty ? 'On Duty' : 'Off Duty'}
                          color={steward.isOnDuty ? 'success' : 'default'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info">No performance data found.</Alert>
          )}
        </TabPanel>

        {/* Attendance Report */}
        <TabPanel value={tabValue} index={3}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : attendanceData ? (
            <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: 600 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>Steward</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>Code</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Present</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Late</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Hours</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Avg/Day</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {attendanceData.stewards?.map((steward: any) => (
                    <TableRow key={steward.stewardId}>
                      <TableCell>{steward.stewardName}</TableCell>
                      <TableCell>{steward.stewardCode}</TableCell>
                      <TableCell align="right">{steward.presentDays}</TableCell>
                      <TableCell align="right">{steward.lateDays}</TableCell>
                      <TableCell align="right">
                        {(steward.totalWorkingMinutes / 60).toFixed(1)}h
                      </TableCell>
                      <TableCell align="right">
                        {steward.presentDays > 0
                          ? ((steward.totalWorkingMinutes / 60) / steward.presentDays).toFixed(1)
                          : 0}h
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info">No attendance data found.</Alert>
          )}
        </TabPanel>

        {/* Settings */}
        <TabPanel value={tabValue} index={4}>
          {config && (
            <Box sx={{ maxWidth: 600 }}>
              <Typography variant="h6" gutterBottom>Attendance Settings</Typography>

              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>Verification Requirements</Typography>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
                  <Box>
                    <Typography variant="body1">Face Recognition</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Require face verification for check-in
                    </Typography>
                  </Box>
                  <Switch
                    checked={config.faceRequired}
                    onChange={(e) => handleConfigChange('faceRequired', e.target.checked)}
                  />
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
                  <Box>
                    <Typography variant="body1">Location Verification</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Require steward to be at venue location
                    </Typography>
                  </Box>
                  <Switch
                    checked={config.locationRequired}
                    onChange={(e) => handleConfigChange('locationRequired', e.target.checked)}
                  />
                </Box>

                {config.locationRequired && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Geofence Radius: {config.geofenceRadius}m
                    </Typography>
                    <TextField
                      type="number"
                      size="small"
                      value={config.geofenceRadius}
                      onChange={(e) => handleConfigChange('geofenceRadius', parseInt(e.target.value))}
                      InputProps={{ endAdornment: 'meters' }}
                      sx={{ width: 150 }}
                    />
                  </Box>
                )}
              </Paper>

              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>Approval Mode</Typography>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
                  <Box>
                    <Typography variant="body1">Auto-Approve</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {config.autoApprove
                        ? 'Check-ins are approved automatically if all verifications pass'
                        : 'All check-ins require manual approval (flags for review)'}
                    </Typography>
                  </Box>
                  <Switch
                    checked={config.autoApprove}
                    onChange={(e) => handleConfigChange('autoApprove', e.target.checked)}
                  />
                </Box>

                {config.faceRequired && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Face Match Threshold: {(config.faceThreshold * 100).toFixed(0)}%
                    </Typography>
                    <TextField
                      type="number"
                      size="small"
                      value={(config.faceThreshold * 100).toFixed(0)}
                      onChange={(e) => handleConfigChange('faceThreshold', parseFloat(e.target.value) / 100)}
                      InputProps={{ endAdornment: '%' }}
                      inputProps={{ min: 50, max: 90, step: 5 }}
                      helperText="Lower = stricter matching (60-70% recommended)"
                      sx={{ width: 150 }}
                    />
                  </Box>
                )}
              </Paper>

              <Paper sx={{ p: 3 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>Audit Trail</Typography>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
                  <Box>
                    <Typography variant="body1">Capture Photo on Check-in</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Store a photo during each check-in for audit purposes
                    </Typography>
                  </Box>
                  <Switch
                    checked={config.capturePhoto}
                    onChange={(e) => handleConfigChange('capturePhoto', e.target.checked)}
                  />
                </Box>
              </Paper>

              <Box sx={{ mt: 3 }}>
                <Button
                  variant="outlined"
                  onClick={() => setLocationDialogOpen(true)}
                  startIcon={<MyLocation />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderColor: theme.gray?.[300] || '#E0E0E0',
                    color: theme.text,
                    '&:hover': {
                      backgroundColor: theme.hover,
                      borderColor: theme.primary
                    }
                  }}
                >
                  {venueLocation?.configured ? 'Update Venue Location' : 'Set Venue Location'}
                </Button>
                {!venueLocation?.configured && (
                  <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
                    Location not configured. Set venue location to enable geofencing.
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </TabPanel>

        {/* Audit History */}
        <TabPanel value={tabValue} index={5}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : auditHistory.length === 0 ? (
            <Alert severity="info">No audit history found.</Alert>
          ) : (
            <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: 600 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>Date/Time</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>Action</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>Steward</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>Reason</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>Performed By</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {auditHistory.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="body2">
                          {format(new Date(log.createdAt), 'dd MMM yyyy')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {format(new Date(log.createdAt), 'hh:mm a')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getActionTypeLabel(log.actionType)}
                          size="small"
                          color={getActionTypeColor(log.actionType)}
                        />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{log.stewardName}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {log.reason || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{log.performedBy}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>
      </Box>

      {/* Actions Menu */}
      <Menu
        anchorEl={actionsAnchorEl}
        open={Boolean(actionsAnchorEl)}
        onClose={handleActionsClose}
      >
        {selectedSteward?.checkedIn && (
          <MenuItem onClick={openResetCheckInDialog}>
            <ListItemIcon><Refresh fontSize="small" /></ListItemIcon>
            <ListItemText>Reset Check-in</ListItemText>
          </MenuItem>
        )}
        {selectedSteward?.checkedOut && (
          <MenuItem onClick={openResetCheckOutDialog}>
            <ListItemIcon><Refresh fontSize="small" /></ListItemIcon>
            <ListItemText>Reset Check-out</ListItemText>
          </MenuItem>
        )}
        {(selectedSteward?.checkedIn || selectedSteward?.checkedOut) && <Divider />}
        {selectedSteward?.attendanceId && (
          <>
            <MenuItem onClick={openAddFlagDialog}>
              <ListItemIcon><Label fontSize="small" /></ListItemIcon>
              <ListItemText>Add Flag</ListItemText>
            </MenuItem>
            <MenuItem onClick={openViewFlagsDialog}>
              <ListItemIcon><History fontSize="small" /></ListItemIcon>
              <ListItemText>View/Remove Flags</ListItemText>
            </MenuItem>
            <Divider />
          </>
        )}
        <MenuItem onClick={openResetFaceDialog}>
          <ListItemIcon><Face fontSize="small" /></ListItemIcon>
          <ListItemText>Reset Face ID</ListItemText>
        </MenuItem>
      </Menu>

      {/* Reset Check-in Dialog */}
      <Dialog open={resetCheckInDialogOpen} onClose={() => setResetCheckInDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reset Check-in</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This will require {selectedSteward?.stewardName} to check in again. Are you sure?
          </Typography>
          <TextField
            label="Reason"
            value={actionReason}
            onChange={(e) => setActionReason(e.target.value)}
            fullWidth
            multiline
            rows={2}
            placeholder="Enter reason for reset (required)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetCheckInDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleResetCheckIn}
            disabled={actionLoading || !actionReason.trim()}
          >
            {actionLoading ? <CircularProgress size={24} /> : 'Reset Check-in'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Check-out Dialog */}
      <Dialog open={resetCheckOutDialogOpen} onClose={() => setResetCheckOutDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reset Check-out</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This will require {selectedSteward?.stewardName} to check out again. Are you sure?
          </Typography>
          <TextField
            label="Reason"
            value={actionReason}
            onChange={(e) => setActionReason(e.target.value)}
            fullWidth
            multiline
            rows={2}
            placeholder="Enter reason for reset (required)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetCheckOutDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleResetCheckOut}
            disabled={actionLoading || !actionReason.trim()}
          >
            {actionLoading ? <CircularProgress size={24} /> : 'Reset Check-out'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Face Dialog */}
      <Dialog open={resetFaceDialogOpen} onClose={() => setResetFaceDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reset Face ID</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This will delete {selectedSteward?.stewardName}'s face data. They will need to re-register their face. Are you sure?
          </Typography>
          <TextField
            label="Reason"
            value={actionReason}
            onChange={(e) => setActionReason(e.target.value)}
            fullWidth
            multiline
            rows={2}
            placeholder="Enter reason for face reset (required)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetFaceDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleResetFace}
            disabled={actionLoading || !actionReason.trim()}
          >
            {actionLoading ? <CircularProgress size={24} /> : 'Reset Face ID'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Flag Dialog */}
      <Dialog open={addFlagDialogOpen} onClose={() => setAddFlagDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Flag</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add a flag to {selectedSteward?.stewardName}'s attendance record.
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Select Flag</InputLabel>
            <Select
              value={selectedFlag}
              onChange={(e) => setSelectedFlag(e.target.value)}
              label="Select Flag"
            >
              {flagOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Box>
                    <Typography variant="body2">{option.value}</Typography>
                    <Typography variant="caption" color="text.secondary">{option.description}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Notes (optional)"
            value={flagNotes}
            onChange={(e) => setFlagNotes(e.target.value)}
            fullWidth
            multiline
            rows={2}
            placeholder="Additional notes about this flag"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddFlagDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddFlag}
            disabled={actionLoading || !selectedFlag}
          >
            {actionLoading ? <CircularProgress size={24} /> : 'Add Flag'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Flags Dialog */}
      <Dialog open={viewFlagsDialogOpen} onClose={() => setViewFlagsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Flags for {selectedSteward?.stewardName}</DialogTitle>
        <DialogContent>
          {currentFlags.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No flags on this record.</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {currentFlags.map((flag, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                  <Chip label={flag} color="warning" />
                  <Button
                    size="small"
                    color="error"
                    onClick={() => handleRemoveFlag(flag.split(':')[0])}
                  >
                    Remove
                  </Button>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewFlagsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Location Dialog */}
      <Dialog open={locationDialogOpen} onClose={() => setLocationDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Set Venue Location</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Set your venue location for steward attendance verification. Stewards can only check in when within the specified radius.
          </Typography>

          <Button
            variant="outlined"
            startIcon={<MyLocation />}
            onClick={handleGetCurrentLocation}
            fullWidth
            sx={{ mb: 3 }}
          >
            Use Current Location
          </Button>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Latitude"
              value={newLatitude}
              onChange={(e) => setNewLatitude(e.target.value)}
              type="number"
              inputProps={{ step: 'any' }}
              fullWidth
            />
            <TextField
              label="Longitude"
              value={newLongitude}
              onChange={(e) => setNewLongitude(e.target.value)}
              type="number"
              inputProps={{ step: 'any' }}
              fullWidth
            />
            <TextField
              label="Radius (meters)"
              value={newRadius}
              onChange={(e) => setNewRadius(e.target.value)}
              type="number"
              helperText="Stewards must be within this radius to check in"
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLocationDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveLocation}
            disabled={savingLocation || !newLatitude || !newLongitude}
          >
            {savingLocation ? <CircularProgress size={24} /> : 'Save Location'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
